/** The Library's results: asset cards (grid), table rows, their marks, and paging as the list scrolls. */
import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Lock, MessageSquareText } from 'lucide-react';
import { TICKS_PER_SECOND, type AssetSummary, type MomentDTO } from '@prism/contracts';
import { setGraphicDrag } from '../../lib/graphic-drag';
import { relative } from '../../lib/format';
import { AssetThumb, markIfComplete, markLoaded } from '../../components/SpriteThumb';
import { AssetStatus, Progress } from '../../components/Status';
import { QcBadge } from '../../components/QualityControl';
import { MasterBadge } from '../../components/MasterBadge';
import { RightsMark } from '../../components/RightsMark';
import type { Column } from '../LibraryColumns';
import type { SelectGesture } from './useLibrarySelection';
import { DeliveryBadge } from '../assignments/AssignForDelivery';

/** The warning shown on an asset whose metadata is incomplete (a needed field is empty). */
function IncompleteMark() {
  return <span className="incomplete-mark" data-testid="incomplete-mark" title="Incomplete metadata: a needed field is empty" role="img" aria-label="Incomplete metadata"><AlertTriangle size={13} /></span>;
}

export function CategoryPill({ category }: { category: { name: string; color: string } }) {
  return <span className="pill category-pill" data-testid="category-pill" style={{ borderColor: category.color, color: category.color }}>{category.name}</span>;
}

/**
 * 1.2.0 multi-select: the hover checkbox on a card and the table's checkbox column. A click on it adds or removes
 * the asset (Shift: the range from the anchor) without opening or moving the Inspector; it does not reach the card.
 */
function SelectCheck({ title, checked, onCheck }: { title: string; checked: boolean; onCheck: (gesture: SelectGesture) => void }) {
  return (
    <input type="checkbox" className="select-check" checked={checked} aria-label={`Select ${title}`} data-testid="asset-check" tabIndex={-1}
      onChange={() => {}} onDoubleClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); onCheck({ shiftKey: e.shiftKey, metaKey: true }); }} />
  );
}

/** A click's held keys (a Shift-click's text selection is prevented on mouse down). */
const gestureOf = (e: React.MouseEvent): SelectGesture => ({ metaKey: e.metaKey, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey });
const noShiftTextSelect = (e: React.MouseEvent) => { if (e.shiftKey) e.preventDefault(); };

/** The last step of the first results' fade-in (1.2.6): cards are 25 ms apart, so the delay is capped at .6 s. */
const ENTRY_LAST_STEP = 24;
/** When the stagger has played and the fade-in order is dropped. */
export const ENTRY_CLEAR_MS = 1500;
/** The fade-in order of the first results: each id's place, those past the cap sharing the last step. */
export const entryOrderOf = (ids: readonly string[]): Map<string, number> => new Map(ids.map((id, i) => [id, Math.min(i, ENTRY_LAST_STEP)]));

/**
 * The cards that fade in, with their place in the stagger: every card of the first results this list shows, those
 * past the cap sharing the last step (the delay of .6 s and the .28 s animation end before the 1.5 s clear-out).
 * Taken once, on the first render that has items, and dropped after the stagger has played, so load more,
 * refetches, a new search and cards shown again later never animate.
 */
export function useEntryOrder(items: readonly { id: string }[]): ReadonlyMap<string, number> {
  const order = useRef<Map<string, number> | null>(null);
  if (order.current === null && items.length > 0) order.current = entryOrderOf(items.map((a) => a.id));
  const taken = order.current !== null;
  useEffect(() => {
    if (!taken) return;
    const t = setTimeout(() => { order.current = new Map(); }, ENTRY_CLEAR_MS);
    return () => clearTimeout(t);
  }, [taken]);
  return order.current ?? EMPTY_ORDER;
}
export const EMPTY_ORDER: ReadonlyMap<string, number> = new Map();

export function AssetCard({ asset, enter, selected, checked, focused, onSelect, onFocus, onCheck, onOpen, moments, onMoment }: {
  asset: AssetSummary;
  /** Its place in the first results' fade-in (useEntryOrder), when it is one of those cards. */
  enter?: number;
  /** Shown as selected (aria-selected): in the selection, or the Inspector's asset while nothing is selected. */
  selected: boolean;
  /** In the selection (its checkbox). */
  checked: boolean;
  /** The one the Inspector and the arrow keys follow (the roving tab stop). */
  focused: boolean;
  onSelect: (gesture: SelectGesture) => void; onFocus: () => void; onCheck: (gesture: SelectGesture) => void;
  onOpen: () => void; moments?: MomentDTO[]; onMoment: (seconds: number, subtitle?: boolean) => void;
}) {
  return (
    <div
      className={enter === undefined ? 'card' : 'card card-enter'}
      style={enter === undefined ? undefined : { '--i': enter } as React.CSSProperties}
      role="option"
      aria-selected={selected}
      tabIndex={focused ? 0 : -1}
      data-asset-id={asset.id}
      data-testid="asset-card"
      title={`${asset.title} · ${asset.assetCode}`}
      onMouseDown={noShiftTextSelect}
      onClick={(e) => onSelect(gestureOf(e))}
      onDoubleClick={onOpen}
      onFocus={(e) => e.target === e.currentTarget && onFocus()}
      // A graphics asset can be dragged onto a sequence's graphics track (in the editor, also in another window).
      draggable={asset.mediaKind === 'graphic' && asset.status === 'ready'}
      onDragStart={asset.mediaKind === 'graphic' ? (e) => setGraphicDrag(e, { assetId: asset.id }, asset.title) : undefined}
    >
      <SelectCheck title={asset.title} checked={checked} onCheck={onCheck} />
      <AssetThumb asset={asset} />
      <div className="card-body">
        <div className="card-title" title={asset.title}>{asset.restricted && <Lock size={11} className="restricted-mark" aria-label="Restricted" />}{asset.title}</div>
        <div className="card-meta">
          <AssetStatus status={asset.status} availability={asset.availability} label={asset.status === 'processing' && asset.progress ? asset.progress.label : undefined} />
          {asset.category && <CategoryPill category={asset.category} />}
          {asset.duplicateOf && <span className="pill" title="Identical content exists in another asset">Duplicate</span>}
          {asset.metadataIncomplete && <IncompleteMark />}
          <RightsMark rights={asset.rights} />
          {asset.qc && <QcBadge status={asset.qc.effectiveStatus} resolved={asset.qc.resolved} compact />}
          <MasterBadge asset={asset} compact />
          {asset.status === 'placeholder' && <DeliveryBadge assetId={asset.id} />}
          <span style={{ flex: 1 }} />
          <span>{relative(asset.createdAt)}</span>
        </div>
        {asset.status === 'failed' && asset.error && <div style={{ fontSize: 12, color: 'var(--danger)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={asset.error.message}>{asset.error.message}</div>}
        <Progress asset={asset} />
        {moments?.slice(0, 2).map((m) => (
          <div key={m.segmentId} className="moment-line" data-testid="moment" data-source={m.source ?? 'stratum'} title={`${m.stratumLabel}: ${m.text || m.values.join(', ')}`}
            onClick={(e) => { e.stopPropagation(); onMoment(m.startTicks / TICKS_PER_SECOND, m.source === 'subtitle'); }}>
            {m.thumbnailUrl && m.source === 'subtitle' && <img className="moment-thumb" src={m.thumbnailUrl} alt="" loading="lazy" />}
            <span className="mono">{m.timecode ?? ''}</span>
            {m.source === 'subtitle' ? <MessageSquareText size={11} aria-label={`Subtitles: ${m.stratumLabel}`} /> : <span style={{ color: m.color }}>●</span>}
            <span>{m.text || m.values.join(', ')}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AssetTable({ items, entry, columns, focusedId, isSelected, isChecked, onSelect, onFocus, onCheck, onCheckAll, onOpen }: {
  items: AssetSummary[]; columns: Column[]; focusedId: string | null;
  /** The first results' fade-in (useEntryOrder), as the grid's cards (1.2.8). */
  entry: ReadonlyMap<string, number>;
  /** As AssetCard's selected and checked. */
  isSelected: (id: string) => boolean; isChecked: (id: string) => boolean;
  onSelect: (id: string, gesture: SelectGesture) => void; onFocus: (id: string) => void; onCheck: (id: string, gesture: SelectGesture) => void;
  /** The header's checkbox: every loaded asset, or none. */
  onCheckAll: (all: boolean) => void; onOpen: (id: string) => void;
}) {
  const checked = items.filter((a) => isChecked(a.id)).length;
  return (
    // asset-table (1.2.8): the Dock-style hover, the rows' fade-in and the thumbnails' fade-in (styles.css, motion).
    <table className="table asset-table" role="grid" aria-label="Assets" aria-multiselectable="true" data-testid="asset-table">
      <thead>
        <tr>
          <th className="select-col">
            <input type="checkbox" className="select-check" aria-label="Select every loaded asset" data-testid="asset-check-all"
              checked={checked > 0 && checked === items.length} ref={(el) => { if (el) el.indeterminate = checked > 0 && checked < items.length; }}
              onChange={(e) => onCheckAll(e.target.checked)} />
          </th>
          <th style={{ width: 88 }} aria-label="Preview" />
          <th>Title</th>
          {columns.map((c) => <th key={c.id}>{c.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {items.map((a) => (
          <tr key={a.id} className={entry.has(a.id) ? 'row-enter' : undefined} style={entry.has(a.id) ? { '--i': entry.get(a.id) } as React.CSSProperties : undefined}
            aria-selected={isSelected(a.id)} tabIndex={a.id === focusedId ? 0 : -1} data-asset-id={a.id} data-testid="asset-row"
            onMouseDown={noShiftTextSelect} onClick={(e) => onSelect(a.id, gestureOf(e))} onDoubleClick={() => onOpen(a.id)} onFocus={(e) => e.target === e.currentTarget && onFocus(a.id)}>
            <td className="select-col"><SelectCheck title={a.title} checked={isChecked(a.id)} onCheck={(g) => onCheck(a.id, g)} /></td>
            {/* Fades in once loaded, as the grid's posters (keyed by its URL so a new thumbnail fades too). */}
            <td className="thumb-col">{a.thumbnailUrl ? <img key={a.thumbnailUrl} className="mini-thumb" src={a.thumbnailUrl} alt="" loading="lazy"
              ref={markIfComplete} onLoad={markLoaded} onError={markLoaded} /> : <div className="mini-thumb" />}</td>
            {/* 1.2.1: cut off with … at a width (table cells ignore max-width), in full on hover. */}
            <td style={{ fontWeight: 550 }} title={a.title}><span className="cell-clip title">{a.restricted && <Lock size={11} className="restricted-mark" aria-label="Restricted" />}{a.title}{a.metadataIncomplete && <IncompleteMark />}<RightsMark rights={a.rights} />{a.qc && <span style={{ marginLeft: 6 }}>
<QcBadge status={a.qc.effectiveStatus} resolved={a.qc.resolved} compact /></span>}<MasterBadge asset={a} compact /></span></td>
            {columns.map((c) => <td key={c.id} className={c.mono ? 'mono' : c.muted ? 'muted' : undefined}><span className="cell-clip">{c.render(a)}</span></td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Fetch the next page when the end of the list scrolls into view (or is near). */
export function useLoadMore(query: { hasNextPage: boolean; isFetchingNextPage: boolean; fetchNextPage: () => Promise<unknown> }) {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const latest = useRef(query);
  latest.current = query;
  useEffect(() => {
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => {
      const q = latest.current;
      if (entries.some((e) => e.isIntersecting) && q.hasNextPage && !q.isFetchingNextPage) void q.fetchNextPage();
    }, { rootMargin: '600px 0px' });
    io.observe(node);
    return () => io.disconnect();
    // Re-observe after each page so a list still shorter than the view keeps loading.
  }, [node, query.hasNextPage, query.isFetchingNextPage]);
  return setNode;
}
